/**
 * `/analytics` — pick two dates and see what actually went out between them,
 * and to how many distinct people.
 *
 * **Both halves of that sentence have been wrong once in this product**, which
 * is why the screen is shaped around them rather than around a chart.
 * Reachability was once a row count wearing the word *reach* — 531 rows, 526
 * distinct addresses, and no type or sentence anywhere that could tell the two
 * apart — and every partition-dating defect in slices 11 and 12 came from a
 * period resolved against the wrong clock. So the two things this page must
 * never get wrong are the unit and the boundaries, and both are stated in words
 * on the screen rather than implied by a number.
 *
 * **One request draws the report.** The alternative — assemble the period in
 * the browser from `/campaigns` and then one results call per campaign — is the
 * N+1 this codebase names in several headers, and it cannot produce the
 * headline at all: distinct recipients across a period is one count of distinct
 * people over one set of rows, and adding two campaigns'
 * reach counts everybody in both of them twice. The sum belongs where the rows
 * are; `generatedAt` on the response is therefore one moment and the report
 * means it. Slice 44's Meta ad account card is not the report — a second
 * read, of Meta, with its own tag — and slice 45's comparison is drawn from
 * the two of them together; see below.
 *
 * **The rules slices 10 to 12 wrote into the Overview's header apply here
 * unchanged**, and two of them do real work on this page:
 *
 *   - *Zero and "not measured" are different.* A channel this workspace does not
 *     send on gets a sentence, never a `0`. A period with no campaigns gets a
 *     sentence, never a grid of noughts. Both distinctions are carried by the
 *     contract — nullable channels, and `NO_CAMPAIGNS_IN_RANGE_NOTE` — so the
 *     screen cannot quietly lose them in a layout change.
 *   - *A rate is never a bare percentage, and a cohort must match its claim.*
 *     There is no bounce rate and no complaint rate on this screen at all. They
 *     are measured against thresholds AWS enforces on email, they need a volume
 *     floor under them, and slice 10 shipped a critical by computing them across
 *     channels. `analyticsReportSchema` carries counts and no `RateReading`,
 *     which is the contract making that decision rather than this file
 *     remembering it.
 *
 * **The picker and the report are deliberately not the same state.** What the
 * form holds is a *request*; what the screen draws is a *response*, and the
 * period printed above the figures comes off the response. A read that fails
 * therefore leaves the previous period's numbers on screen still labelled with
 * the previous period, under an error that says the refresh failed — rather than
 * relabelling old figures with the dates somebody just asked for and did not
 * get. That is the same reason `useAsyncData` keeps data across a refetch, and
 * on this page it is the difference between a stale screen and a lying one.
 *
 * **A period that cannot be resolved is not approximated.** `browserTimeZone()`
 * names the zone and says whether `Intl` actually resolved it; when the presets
 * cannot be worked out at all there is no request and the screen says so. UTC is
 * not a fallback here for the reason the contract gives: a period bucketed
 * against the wrong clock is indistinguishable from a correct one once it is on
 * screen.
 *
 * **The zone is this device's, and that is a limitation rather than a design.**
 * `analyticsRangeRequestSchema` requires a zone and refuses to default one, on
 * the argument that the caller knows the tenant's; there is no timezone on
 * `tenants` and none in any contract, so what this page actually knows is what
 * `Intl` says about the machine it is running on, and `AnalyticsRangePicker`
 * offers no control to change it. An operator in Mumbai and their agency in
 * London pick the same two dates and get different campaign lists — the same
 * divergence `App.tsx` refuses a shareable `/analytics/:from/:to` route to avoid,
 * arriving through the device instead. It is named on the screen, in
 * `AnalyticsCopy`, because a figure whose period depends on who is looking must
 * not be presented as the workspace's. The fix is a workspace timezone, and it
 * is a column this slice did not add.
 *
 * **Nothing on this page can send anything.** It is a read over campaigns and
 * delivery events that already happened, and of Meta's figures for the ad
 * account; there is no address, no phone number
 * and no recipient list anywhere on it, and the only contact-shaped figures are
 * counts of distinct addresses.
 *
 * **Slice 44 adds a second read, of Meta**, for the ad surfaces: the same
 * two days the form holds, asked of Meta in the ad account's own zone, drawn
 * in its own card after the channels with the days it asked for. The ad
 * account is read first, off the integrations list, and only a workspace with
 * one connected asks Meta at all — so a workspace that does not use Meta gets
 * no card, spends none of the Meta read limit, and never calls a route a
 * database the Meta migrations have not reached cannot answer. It is fetched
 * here and handed to `AnalyticsAds` as a state, so the card renders in the
 * screen suite without a browser. A read that failed is drawn as "Not read",
 * never as the period before it; and the card is drawn inside the report, so
 * when the report itself did not load there is one alert on the page, not two.
 *
 * **Slice 45 sets the surfaces side by side under it** — `AnalyticsBySurface`,
 * each surface's reach against spend where money is measured — from the
 * report and the ad read this page already holds, with no request of its own.
 *
 * **Slice 17 cut the standfirst under the title from four claims to two, and
 * neither of the two that went was lost.** *Reach counts distinct addresses
 * rather than rows* and *the period is read in one named timezone with its
 * boundaries printed* were both being said twice: once here, as a promise about
 * what the screen will do, and again below as the thing itself, against the
 * figure. The second is where a reader needs it — a unit printed at the top of
 * the page is not a unit — so the promise went and the statements stayed. What
 * kept its place is the pair a reader arrives without: that each channel has its
 * own vocabulary, and that there is no combined failure figure, because the
 * absence of a number is the one thing a screen cannot demonstrate by showing
 * it.
 */
import { analyticsRangeRequestSchema, type AnalyticsRangeRequest } from '@wend/shared';
import { Card, CardContent, Spinner } from '@wend/ui';
import { useMemo, useState } from 'react';

import { AppLayout } from '../../components/AppLayout';
import { ErrorState } from '../../components/ErrorState';
import { analyticsDatesForChoice } from '../../lib/analytics-display';
import { fetchAnalyticsReport } from '../../lib/api/analytics';
import { fetchMetaAdsIntegration, fetchMetaAdsPeriod } from '../../lib/api/meta-ads';
import { browserTimeZone } from '../../lib/campaign-display';
import { useAsyncData } from '../../lib/use-async-data';
import { AnalyticsAds } from './AnalyticsAds';
import { AnalyticsBySurface } from './AnalyticsBySurface';
import { AnalyticsRangePicker } from './AnalyticsRangePicker';
import { AnalyticsReportView } from './AnalyticsReportView';

/**
 * The period the screen opens on: the thirty days up to yesterday.
 *
 * Closed days, so the first thing anybody sees is a period that will still mean
 * the same thing this evening — the same argument slice 13 made for scheduling
 * only closed windows, applied to a default. `null` when the platform will not
 * resolve the zone, in which case the screen asks rather than guesses.
 */
function initialRequest(timezone: string): AnalyticsRangeRequest | null {
  const dates = analyticsDatesForChoice('last_30_days', new Date(), timezone, {
    fromDate: '',
    toDate: '',
  });
  if (dates === null) return null;

  const parsed = analyticsRangeRequestSchema.safeParse({ ...dates, timezone });
  return parsed.success ? parsed.data : null;
}

export function AnalyticsPage() {
  // Reused rather than re-derived: `resolved: false` means Intl would not name
  // this browser's zone, which is a sentence the screen owes the reader.
  const zone = useMemo(browserTimeZone, []);

  const [request, setRequest] = useState<AnalyticsRangeRequest | null>(() =>
    initialRequest(zone.timezone),
  );

  const { data, error, status, loadingFirst, reload } = useAsyncData(
    async (signal) => (request === null ? null : fetchAnalyticsReport(request, signal)),
    [request],
  );
  /* The ad account first: without one, there is nothing to ask Meta. */
  const integration = useAsyncData(async (signal) => ({ row: await fetchMetaAdsIntegration(signal) }), []);
  const account = integration.data?.row ?? null;
  /* The ad surfaces: the same two days, asked of Meta. */
  const ads = useAsyncData(
    async (signal) =>
      request === null || account === null
        ? null
        : fetchMetaAdsPeriod({ fromDate: request.fromDate, toDate: request.toDate }, signal),
    [request, account === null],
  );

  return (
    <AppLayout title="Analytics">
      <div className="flex flex-col gap-6">
        <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
          What went out in a period, and how many different people it reached. Each channel keeps
          its own words for what happened; there is no combined failure figure.
        </p>

        <AnalyticsRangePicker
          timezone={zone.timezone}
          zoneResolved={zone.resolved}
          applied={request}
          pending={status === 'loading' && request !== null}
          onApply={setRequest}
        />

        {request === null && (
          <Card>
            <CardContent className="py-8">
              <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
                Wend could not work out what a period means in {zone.timezone} on this device, so
                nothing has been read. Pick two dates above. If those are refused too, this browser
                is not resolving timezones and no figure here could be trusted to cover the days it
                claimed.
              </p>
            </CardContent>
          </Card>
        )}

        {request !== null && loadingFirst && (
          <Card>
            <CardContent className="flex items-center gap-3 py-10 text-[0.9rem] text-muted">
              <Spinner size="sm" label={null} />
              <span role="status">Reading this period…</span>
            </CardContent>
          </Card>
        )}

        {request !== null && status === 'error' && data === null && (
          <Card>
            <CardContent>
              <ErrorState
                error={error}
                title="That period did not load"
                onRetry={reload}
                className="flex flex-col items-start gap-3 px-1 py-6"
              />
            </CardContent>
          </Card>
        )}

        {data !== null && (
          <>
            {status === 'error' && (
              <ErrorState
                error={error}
                title="Could not read the period again"
                onRetry={reload}
                className="flex flex-col items-start gap-3 rounded-base border border-line px-5 py-4"
              />
            )}

            {/* The figures below are the ones that loaded, under the period they
                were read for — which is the response's, never the form's. */}
            <AnalyticsReportView
              report={data}
              zoneResolved={zone.resolved}
              ads={
                account === null ? null : (
                  <AnalyticsAds
                    state={ads.data}
                    unread={ads.status === 'error'}
                    onRetry={ads.reload}
                    accountName={account.accountName}
                    pageZone={zone.timezone}
                  />
                )
              }
              bySurface={
                account === null ? null : (
                  /* A failed read is no read here: the comparison is never drawn from figures the card above calls "Not read". */
                  <AnalyticsBySurface report={data} ads={ads.status === 'error' ? null : ads.data} />
                )
              }
            />
          </>
        )}
      </div>
    </AppLayout>
  );
}
